<template>
  <div :class="[
    'newsletter q-pa-md flex wrap',
    { 'render': !isPreview },
    { 'border-radius-12': !isNegative && !isColumnLayout },
    isHorizontal ? 'horizontal items-center' : 'column',
    isNegative ? 'bg-negative' : 'bg-card',
  ]">
    <NewsletterIcon class="paper-plane q-mb-sm" />
    <div class="flex column width-fit col newsletter-body">
      <div :class="['flex no-wrap', isHorizontal ? 'justify-between' : 'column']">
        <div class="fs-16 text-weight-medium q-mb-sm newsletter-title main-text">
          {{ metaFields.newsletterTitle }}
        </div>
        <div class="fs-8 q-mb-sm newsletter-description main-text opacity">
          {{ metaFields.newsletterDescription }}
        </div>
      </div>
      <div :class="['flex', isHorizontal ? 'q-mt-sm' : 'column']">
        <div
          v-if="isPreview"
          :class="[
            'your-email fs-10 flex justify-between items-center border-radius-8 q-pa-sm q-mb-sm main-text opacity',
            { 'col': isHorizontal },
          ]"
        >
          {{ $t('text.yourEmail') }}
          <q-icon
            name="far fa-envelope"
            size="12px"
            class="icon"
          />
        </div>
        <q-form
          v-else
          ref="emailFormRef"
          class="col"
        >
          <AppInput
            v-model="email"
            :placeholder="$t('text.yourEmail')"
            outlined
            dense
            :rules="[rules.EMAIL, rules.REQUIRED]"
            lazy-rules
          >
            <template #append>
              <EnvelopeIcon class="icon" />
            </template>
          </AppInput>
        </q-form>
        <AppBtn
          :class="['text-button border-radius-8 q-mb-sm', isPreview ? 'fs-12' : 'fs-16']"
          :padding="isPreview ? '8px' : '16px 20px'"
          size="sm"
          @click="subscribe"
        >
          {{ metaFields.newsletterTextButton }} <CheckmarkIcon class="q-ml-sm" />
        </AppBtn>
      </div>
      <div
        v-if="metaFields.newsletterLinkDataRules"
        class="fs-6 main-text opacity"
      >
        {{ $t('text.byClickingSubscribe') }}
        <span
          v-t="'text.dataProcessingRules'"
          class="primary-text cursor-pointer"
          @click="onClickRules"
        />.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import NewsletterIcon from 'components/icons/NewsletterIcon.vue';
import EnvelopeIcon from 'components/icons/EnvelopeIcon.vue';
import CheckmarkIcon from 'components/icons/CheckmarkIcon.vue';
import AppBtn from 'components/AppBtn.vue';
import AppInput from 'components/AppInput.vue';
import rules from 'src/helpers/rules';

const props = defineProps({
  metaFields: {
    type: Object,
    required: true,
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
  isHorizontal: {
    type: Boolean,
    default: false,
  },
  isNegative: {
    type: Boolean,
    default: false,
  },
  isColumnLayout: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['subscribe']);

const email = ref(null);
const emailFormRef = ref(null);

async function subscribe() {
  if (!props.isPreview) {
    const isValid = await emailFormRef.value.validate();
    if (isValid) {
      emit('subscribe', email.value);
    }
  }
}

function onClickRules() {
  window.open(props.metaFields.newsletterLinkDataRules, '_blank');
}
</script>

<style lang="scss" scoped>
.newsletter {
  text-align: left;
  max-width: 190px;
  margin: 0 auto;
  margin-top: 16px;
  overflow: hidden;

  .primary-text {
    color: var(--blog-primary-color, $primary);
  }

  .main-text {
    color: var(--blog-font-color, #000);

    &.opacity {
      opacity: 0.5;
    }
  }

  .icon {
    color: var(--blog-icon-color, #000);
  }

  &.bg-card {
    background: var(--blog-card-color, #fff);
  }

  .fs-16 {
    line-height: 18px;
  }

  .paper-plane {
    width: 190px;
    color: var(--blog-primary-color, $primary);
  }

  .your-email  {
    border: 1px solid  var(--blog-border-color, $grey-2);
    font-weight: bold;
  }

  .text-button {
    width: fit-content;
    background: var(--blog-primary-color, $primary);
    font-weight: bold;

    :deep(.q-btn__content) {
      color: var(--blog-font-color, #000);
      filter: invert(1);
    }
  }

  .width-fit {
    width: fit-content;
  }

  :deep(.q-field__native) {
    font-weight: bold;
    color: var(--blog-font-color, #000);
    opacity: 0.5;

    &::placeholder {
      color: red;
      opacity: 0.5;
    }
  }

  &.render {
    max-width: unset;
    margin: unset;
    margin-top: 16px;

    .paper-plane {
      width: 290px;
      height: 130px;
    }

    .newsletter-title {
      font-size: 22px !important;
      font-weight: 700;
      line-height: 30px;
    }

    .fs-8 {
      font-size: 12px !important;
      font-weight: 700;
      line-height: 19px;
    }

    .fs-6 {
      font-size: 10px !important;
      font-weight: 700;
      line-height: 15px;
    }
  }

  &.horizontal {
    max-width: unset;
    padding: 40px 36px 40px 16px !important;

    .paper-plane {
      margin-right: 16px;
    }

    .newsletter-title {
      max-width: 200px;
    }

    .newsletter-description {
      max-width: 200px;
      text-align: right;
    }

    .text-button {
      height: 40px;
      margin-left: 20px;
      padding: 0 24px !important;
    }
  }

  &.bg-negative {
    background: var(--blog-primary-color, $primary) !important;

    .paper-plane {
      color: #fff;
    }

    .newsletter-body {
      background: var(--blog-card-color, #fff) !important;;
      padding: 16px;
    }

    &.render {
      padding: 60px;

      .newsletter-body {
        padding: 60px;
      }

      .paper-plane {
        height: 165px;
        width: 40%;
      }
    }
  }

  :deep(.q-field__bottom) {
    padding: 4px 8px 4px;
  }
}

:deep(.q-placeholder) {
  &::placeholder {
    color: var(--blog-font-color, #000);
    opacity: 0.5 !important;
  }
}
</style>
